import React, { useState, useEffect } from 'react';
import { RefreshCw, Download, Calendar, TrendingUp, TrendingDown, Filter } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function BankAccountMovements() {
  const [selectedAccount, setSelectedAccount] = useState('01380010320100324207'); // Pooling por defecto
  const [movements, setMovements] = useState([]);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [serviceAvailable, setServiceAvailable] = useState(true);
  const [days, setDays] = useState(7);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  const accounts = [
    { value: '01380010320100324207', label: 'Cuenta Operativa' },
    { value: '01380010330100324070', label: 'Cuenta Comisiones' },
    { value: '01380001440010066240', label: 'Cuenta Receptora' }
  ];

  const daysOptions = [
    { value: 1, label: 'Hoy' },
    { value: 7, label: 'Últimos 7 días' },
    { value: 15, label: 'Últimos 15 días' },
    { value: 30, label: 'Últimos 30 días' }
  ];

  const fetchMovements = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/super-admin/bank-accounts/movements?account=${selectedAccount}&days=${days}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMovements(response.data.movements || []);
      setBalance(response.data.balance);
      setDateRange(response.data.date_range || { start: '', end: '' });
      setServiceAvailable(response.data.service_available);
      
      if (response.data.error) {
        setError(response.data.error);
      }
    } catch (err) {
      console.error('Error fetching movements:', err);
      setError('Error al cargar movimientos');
      setServiceAvailable(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMovements();
  }, [selectedAccount, days]);

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('es-VE', {
      style: 'currency',
      currency: 'VES',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateStr, horaStr) => {
    if (!dateStr) return 'N/A';
    // Banco Plaza devuelve fechas como YYYY-MM-DD (sin hora). Interpretarlas con
    // new Date() trata "YYYY-MM-DD" como UTC midnight → en TZ Caracas (UTC-4)
    // muestra el día anterior. Renderizamos sin re-convertir timezone porque los
    // datos del banco ya están en hora local VE.
    let dPart = String(dateStr).trim();
    if (dPart.includes('T')) dPart = dPart.split('T')[0];
    if (dPart.includes('/')) {
      const parts = dPart.split('/');
      if (parts.length === 3 && parts[2].length === 4) {
        dPart = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
      }
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dPart)) return String(dateStr);
    const [y, m, d] = dPart.split('-');
    let timePart = '';
    if (horaStr) {
      const h = String(horaStr).trim();
      if (/^\d{6}$/.test(h)) timePart = `${h.slice(0, 2)}:${h.slice(2, 4)}`;
      else if (/^\d{4}$/.test(h)) timePart = `${h.slice(0, 2)}:${h.slice(2, 4)}`;
      else if (/^\d{2}:\d{2}/.test(h)) timePart = h.slice(0, 5);
    }
    return timePart ? `${d}/${m}/${y} ${timePart}` : `${d}/${m}/${y}`;
  };

  const getMovementIcon = (naturaleza) => {
    if (naturaleza === 'CR' || naturaleza === 'C') {
      return <TrendingUp className="h-5 w-5 text-green-500" />;
    }
    return <TrendingDown className="h-5 w-5 text-red-500" />;
  };

  const getMovementClass = (naturaleza) => {
    if (naturaleza === 'CR' || naturaleza === 'C') {
      return 'text-green-600';
    }
    return 'text-red-600';
  };

  const exportToCSV = () => {
    const csvContent = [
      ['Fecha', 'Descripción', 'Referencia', 'Tipo', 'Monto'],
      ...movements.map(m => [
        m.fecha || '',
        m.descripcion || '',
        m.referencia || '',
        m.naturaleza === 'CR' ? 'Crédito' : 'Débito',
        m.monto || ''
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `movimientos_${selectedAccount}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Movimientos Bancarios</h2>
        <p className="text-sm text-gray-500">
          Consulta el historial de transacciones en las cuentas de Caura Pay
        </p>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Cuenta
          </label>
          <select
            value={selectedAccount}
            onChange={(e) => setSelectedAccount(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {accounts.map(acc => (
              <option key={acc.value} value={acc.value}>
                {acc.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Período
          </label>
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {daysOptions.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-end gap-2">
          <button
            onClick={fetchMovements}
            disabled={loading}
            className="flex-1 flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </button>
          <button
            onClick={exportToCSV}
            disabled={movements.length === 0}
            className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Exportar a CSV"
          >
            <Download className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Service Not Available Alert */}
      {!serviceAvailable && (
        <div className="mb-6 bg-yellow-50 border-l-4 border-yellow-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                <strong>Servicio no activado:</strong> {error || 'Los servicios de consulta de movimientos de Banco Plaza no están activos. Contacta al soporte de Banco Plaza para habilitar los permisos READ en tu API key.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Balance Summary */}
      {balance !== null && (
        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-4 mb-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm opacity-90">Saldo Disponible</p>
              <p className="text-3xl font-bold mt-1">{formatCurrency(balance)}</p>
            </div>
            {dateRange.start && (
              <div className="text-right">
                <p className="text-xs opacity-75">Período consultado</p>
                <p className="text-sm opacity-90">
                  {dateRange.start} - {dateRange.end}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Movements List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Cargando movimientos...</span>
        </div>
      ) : movements.length === 0 ? (
        <div className="text-center py-12">
          <Calendar className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Sin movimientos</h3>
          <p className="mt-1 text-sm text-gray-500">
            No se encontraron transacciones en el período seleccionado
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fecha
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Descripción
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Referencia
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Monto
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {movements.map((movement, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                    {formatDate(movement.fecha, movement.hora)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {movement.descripcion || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                    {movement.referencia || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex items-center">
                      {getMovementIcon(movement.naturaleza)}
                      <span className="ml-2">
                        {movement.naturaleza === 'CR' || movement.naturaleza === 'C' ? 'Crédito' : 'Débito'}
                      </span>
                    </div>
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm text-right font-semibold ${getMovementClass(movement.naturaleza)}`}>
                    {movement.naturaleza === 'CR' || movement.naturaleza === 'C' ? '+' : '-'}
                    {formatCurrency(Math.abs(movement.monto || 0))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
